/* Los mandos del catalogo: «Filtros», «Ordenar» y el buscador.
 *
 * Del marco PRODUCTOS del Figma y del comentario #37 de Elisabeth (22/09/2026).
 * El panel es el `#filters-drawer` de Horizon, que ya trae las facetas, el
 * rango de precio y el «Ordenar»; aqui solo se abre y, cuando se pulsa
 * «Ordenar», se lleva la vista a ese bloque dentro del panel.
 *
 * Los oyentes van por DELEGACION en `document`: `theme dev` sustituye la
 * seccion entera al recargar en caliente y los oyentes colgados del nodo se
 * pierden (memoria `oyentes-por-delegacion-con-theme-dev`).
 */
(() => {
  const PANEL = '#filters-drawer';

  const abrirPanel = () => {
    const drawer = document.querySelector(PANEL);
    if (!drawer) return null;
    // `theme-drawer` de Horizon expone `open()`; si el elemento personalizado
    // aun no se ha definido, se cae al `<dialog>` de dentro.
    if (typeof drawer.open === 'function') drawer.open();
    else drawer.querySelector('dialog')?.showModal();
    return drawer;
  };

  document.addEventListener('click', (ev) => {
    const boton = ev.target.closest('[data-nn-mando]');
    if (!boton) return;
    ev.preventDefault();
    const drawer = abrirPanel();
    if (!drawer || boton.dataset.nnMando !== 'ordenar') return;
    // El «Ordenar» es lo ultimo del panel: se le lleva la vista para que no
    // haya que buscarlo bajo todas las facetas.
    requestAnimationFrame(() => {
      drawer
        .querySelector('.sorting-filter, [class*="sorting"]')
        ?.scrollIntoView({ block: 'nearest' });
    });
  });

  // Un clic en el velo cierra el panel. El velo es un pseudoelemento del
  // `body` (seccion 54 del CSS), asi que no recibe eventos: lo que se mira es
  // si el clic cayo fuera del panel con el panel abierto.
  document.addEventListener('click', (ev) => {
    const drawer = document.querySelector(PANEL);
    const dialogo = drawer?.querySelector('dialog');
    if (!dialogo?.open) return;
    if (ev.target.closest('#filters-drawer') || ev.target.closest('[data-nn-mando]')) return;
    if (typeof drawer.close === 'function') drawer.close();
    else dialogo.close();
  });

  /* --- El buscador ------------------------------------------------------
   * Filtra las tarjetas ya pintadas. No toca la URL ni lanza una busqueda al
   * servidor: con dieciocho productos, sacar al comprador del catalogo para
   * ensenarle una pagina de resultados es peor que esconder lo que no encaja.
   */
  const sinTildes = (t) =>
    t
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');

  const filtrar = (texto) => {
    const rejilla = document.querySelector('[id$="__main"] .product-grid');
    if (!rejilla) return;
    const busca = sinTildes(texto.trim());
    const tarjetas = rejilla.querySelectorAll('.product-grid__item, li');
    let vistas = 0;
    tarjetas.forEach((tarjeta) => {
      const encaja = busca === '' || sinTildes(tarjeta.textContent).includes(busca);
      tarjeta.toggleAttribute('data-nn-oculta', !encaja);
      if (encaja) vistas += 1;
    });
    const vacio = document.querySelector('[data-nn-mandos-vacio]');
    if (vacio) vacio.hidden = vistas > 0 || busca === '';
  };

  /* Abrir y cerrar el buscador. SOLO EN MOVIL (Fran, 22/09/2026: «el
   * buscador, cuando este en ordenador, no tiene que colapsar el boton de
   * filtros y ordenar; es mas, tiene que salir completo "Encuentra tu
   * producto". Lo unico que tiene que salir asi cuando este en movil»). A 390
   * el campo esta en reposo como un circulo de lupa; al pulsarlo crece hacia
   * la izquierda y los dos mandos se quedan en su icono. Se cierra con la
   * propia lupa, con Escape o al hacer clic fuera, y SOLO si no hay nada
   * escrito: cerrarlo con una busqueda puesta dejaria la rejilla filtrada sin
   * decir por que. En escritorio el campo ya esta abierto y la lupa solo le
   * lleva el foco.
   */
  const esMovil = () => window.matchMedia('(max-width: 749px)').matches;
  const caja = () => document.querySelector('[data-nn-buscador-caja]');

  const abrirBuscador = (abre) => {
    const c = caja();
    if (!c) return;
    const fila = c.closest('[data-nn-mandos]');
    const campo = c.querySelector('[data-nn-buscador]');
    const lupa = c.querySelector('[data-nn-buscador-abre]');
    c.toggleAttribute('data-abierto', abre);
    fila?.toggleAttribute('data-buscando', abre);
    lupa?.setAttribute('aria-expanded', String(abre));
    if (campo) {
      campo.tabIndex = abre ? 0 : -1;
      if (abre) campo.focus();
      else if (campo.value !== '') {
        campo.value = '';
        filtrar('');
      }
    }
  };

  document.addEventListener('click', (ev) => {
    if (ev.target.closest('[data-nn-buscador-abre]')) {
      const c = caja();
      if (!esMovil()) {
        c?.querySelector('[data-nn-buscador]')?.focus();
        return;
      }
      abrirBuscador(!c?.hasAttribute('data-abierto'));
      return;
    }
    if (!esMovil()) return;
    const c = caja();
    if (!c?.hasAttribute('data-abierto')) return;
    if (ev.target.closest('[data-nn-buscador-caja]')) return;
    // Con algo escrito se queda abierto: la rejilla esta filtrada y el campo
    // es lo unico que lo explica.
    if (c.querySelector('[data-nn-buscador]')?.value.trim() !== '') return;
    abrirBuscador(false);
  });

  document.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Escape' || !esMovil()) return;
    if (!caja()?.hasAttribute('data-abierto')) return;
    abrirBuscador(false);
  });

  // Al pasar de movil a escritorio, el campo tiene que quedarse abierto y los
  // rotulos vueltos: si no, se queda el estado de movil pegado.
  const alCambiarDeAncho = () => {
    if (esMovil()) return;
    const c = caja();
    if (!c) return;
    c.removeAttribute('data-abierto');
    c.closest('[data-nn-mandos]')?.removeAttribute('data-buscando');
    const campo = c.querySelector('[data-nn-buscador]');
    if (campo) campo.tabIndex = 0;
    c.querySelector('[data-nn-buscador-abre]')?.setAttribute('aria-expanded', 'true');
  };
  window.matchMedia('(max-width: 749px)').addEventListener('change', alCambiarDeAncho);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', alCambiarDeAncho);
  else alCambiarDeAncho();

  document.addEventListener('input', (ev) => {
    if (!ev.target.matches('[data-nn-buscador]')) return;
    filtrar(ev.target.value);
  });

  // Enter no debe enviar nada ni recargar: el filtrado ya esta hecho.
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' && ev.target.matches('[data-nn-buscador]')) ev.preventDefault();
  });

  /* --- La cuenta de filtros puestos -------------------------------------
   * Horizon la pinta dentro del panel (`#filters-drawer-bubble`); aqui se
   * repite en el boton para que se vea sin abrirlo. Se vuelve a leer cada vez
   * que la seccion se repinta, que es lo que hacen las facetas al aplicarse.
   */
  const pintarCuenta = () => {
    const boton = document.querySelector('[data-nn-mandos-cuenta]');
    if (!boton) return;
    const activos = document.querySelectorAll(
      '[id$="__main"] .facets__form input[type="checkbox"]:checked'
    ).length;
    boton.textContent = activos > 0 ? String(activos) : '';
    boton.hidden = activos === 0;
  };

  const vigilar = () => {
    const seccion = document.querySelector('[id$="__main"]');
    if (!seccion) return;
    new MutationObserver(pintarCuenta).observe(seccion, { childList: true, subtree: true });
    pintarCuenta();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', vigilar);
  else vigilar();
})();
